/**
 * Utilitaires pour la gestion de la récurrence des rendez-vous
 *
 * Ce fichier centralise toute la logique de génération et de validation
 * des rendez-vous récurrents (journaliers, hebdomadaires, mensuels).
 *
 * Utilisé par: ClientDayView, ClientWeekView, ClientMonthView
 */

import { doc, collection } from 'firebase/firestore';

/**
 * Calcule quelle occurrence d'un jour de la semaine une date représente dans son mois
 * Par exemple: le 3ème mardi du mois
 *
 * @param {Date} date - La date à analyser
 * @returns {number} - Le numéro d'occurrence (1-5)
 */
export const getWeekOfMonth = (date) => {
  const dayOfWeek = date.getDay();
  const dayOfMonth = date.getDate();

  // Compter combien de fois ce jour de la semaine apparaît avant ou à cette date
  let count = 0;
  for (let d = 1; d <= dayOfMonth; d++) {
    const testDate = new Date(date.getFullYear(), date.getMonth(), d);
    if (testDate.getDay() === dayOfWeek) {
      count++;
    }
  }

  return count;
};

/**
 * Vérifie si une date est la dernière occurrence d'un jour de la semaine dans son mois
 * Par exemple: le dernier vendredi du mois
 *
 * @param {Date} date - La date à vérifier
 * @returns {boolean} - true si c'est la dernière occurrence
 */
export const isLastOccurrenceOfDayInMonth = (date) => {
  const dayOfWeek = date.getDay();
  const nextWeek = new Date(date);
  nextWeek.setDate(date.getDate() + 7);

  // Si on change de mois en ajoutant 7 jours, c'est la dernière occurrence
  return nextWeek.getMonth() !== date.getMonth();
};

/**
 * Calcule la date de fin pour une série de rendez-vous récurrents
 *
 * @param {Object} appointmentData - Les données du rendez-vous avec la configuration de récurrence
 * @returns {Date} - La date de fin calculée
 */
export const getRecurrenceEndDate = (appointmentData) => {
  const startDate = new Date(appointmentData.date);

  if (appointmentData.recurrenceEndType === 'on' && appointmentData.recurrenceEndDate) {
    const endDate = new Date(appointmentData.recurrenceEndDate);
    endDate.setHours(23, 59, 59, 999);
    return endDate;
  } else if (appointmentData.recurrenceEndType === 'after') {
    const maxOccurrences = appointmentData.recurrenceEndAfter;
    const endDate = new Date(startDate);

    if (appointmentData.recurrenceBase === 'daily') {
      // Pour journalier: calcul simple
      endDate.setDate(startDate.getDate() + (maxOccurrences - 1) * appointmentData.recurrenceFrequency);
      return endDate;
    } else if (appointmentData.recurrenceBase === 'weekly') {
      // Pour hebdomadaire: calculer combien de semaines nécessaires
      const daysPerWeek = appointmentData.recurrenceDay.length;
      if (daysPerWeek === 0) {
        // Fallback si aucun jour sélectionné
        return new Date(startDate.getFullYear() + 1, startDate.getMonth(), startDate.getDate());
      }
      const weeksNeeded = Math.ceil(maxOccurrences / daysPerWeek);
      endDate.setDate(startDate.getDate() + (weeksNeeded * 7 * appointmentData.recurrenceFrequency));
      return endDate;
    } else if (appointmentData.recurrenceBase === 'monthly') {
      // Pour mensuel: ajouter les mois
      endDate.setMonth(startDate.getMonth() + (maxOccurrences * appointmentData.recurrenceFrequency));
      return endDate;
    }
  }

  // Type 'never': définir une limite raisonnable (2 ans)
  return new Date(startDate.getFullYear() + 2, startDate.getMonth(), startDate.getDate());
};

/**
 * Vérifie si un rendez-vous mensuel doit être ajouté à une date donnée
 *
 * @param {Date} date - La date à vérifier
 * @param {Object} appointmentData - Les données du rendez-vous avec la configuration de récurrence
 * @returns {boolean} - true si le rendez-vous doit être créé à cette date
 */
export const shouldAddAppointment = (date, appointmentData) => {
  if (appointmentData.recurrenceMonthType === 'dayOfWeek') {
    // Type: Nième jour de la semaine du mois (ex: 2ème mardi)
    const targetWeek = parseInt(appointmentData.recurrenceMonthWeek, 10);
    const targetWeekday = parseInt(appointmentData.recurrenceMonthWeekday, 10);

    // Vérifier le jour de la semaine
    if (date.getDay() !== targetWeekday) {
      return false;
    }

    // Si targetWeek === 5, ça signifie "dernier"
    if (targetWeek === 5) {
      return isLastOccurrenceOfDayInMonth(date);
    } else {
      const weekOfMonth = getWeekOfMonth(date);
      return weekOfMonth === targetWeek;
    }
  } else {
    // Type: Jour du mois (ex: le 15)
    const targetDay = parseInt(appointmentData.recurrenceMonthDay, 10);
    return date.getDate() === targetDay;
  }
};

/**
 * Calcule la prochaine date de récurrence pour un rendez-vous mensuel
 *
 * @param {Date} date - La date actuelle
 * @param {Object} appointmentData - Les données du rendez-vous avec la configuration de récurrence
 * @returns {Date} - La prochaine date calculée
 */
export const getNextRecurrenceDate = (date, appointmentData) => {
  const newDate = new Date(date);
  const frequency = parseInt(appointmentData.recurrenceFrequency, 10);

  if (appointmentData.recurrenceBase === 'monthly') {
    // Avancer au mois suivant selon la fréquence
    newDate.setMonth(newDate.getMonth() + frequency);

    if (appointmentData.recurrenceMonthType === 'dayOfWeek') {
      // Type: Nième jour de la semaine (ex: 2ème mardi)
      const targetWeek = parseInt(appointmentData.recurrenceMonthWeek, 10);
      const targetWeekday = parseInt(appointmentData.recurrenceMonthWeekday, 10);

      // Se placer au 1er du mois
      newDate.setDate(1);

      if (targetWeek === 5) {
        // Cas "dernier": trouver le dernier jour de la semaine du mois
        // Aller à la fin du mois
        newDate.setMonth(newDate.getMonth() + 1);
        newDate.setDate(0); // Dernier jour du mois précédent

        // Reculer jusqu'au bon jour de la semaine
        while (newDate.getDay() !== targetWeekday) {
          newDate.setDate(newDate.getDate() - 1);
        }
      } else {
        // Cas normal: trouver le Nième jour de la semaine
        let occurrenceCount = 0;

        while (occurrenceCount < targetWeek) {
          if (newDate.getDay() === targetWeekday) {
            occurrenceCount++;
            if (occurrenceCount === targetWeek) {
              break;
            }
          }
          newDate.setDate(newDate.getDate() + 1);
        }
      }
    } else {
      // Type: Jour du mois (ex: le 15)
      const targetDay = parseInt(appointmentData.recurrenceMonthDay, 10);
      newDate.setDate(targetDay);

      // Gérer les cas où le jour n'existe pas dans le mois (ex: 31 février)
      if (newDate.getDate() !== targetDay) {
        // Le mois n'a pas assez de jours, prendre le dernier jour
        newDate.setDate(0);
      }
    }
  }

  return newDate;
};

/**
 * Génère tous les rendez-vous d'une série récurrente
 *
 * @param {Object} appointmentData - Les données du rendez-vous avec la configuration de récurrence
 * @param {Object} db - Instance Firestore
 * @returns {Array} - Tableau des rendez-vous à créer
 */
export const generateRecurringAppointments = (appointmentData, db) => {
  const appointments = [];
  let currentDate = new Date(appointmentData.date);
  const endDate = getRecurrenceEndDate(appointmentData);

  // Générer un ID de série unique pour lier tous les rendez-vous
  const recurrenceSeriesId = doc(collection(db, 'appointments')).id;

  let occurrenceCount = 0;
  const maxOccurrences = appointmentData.recurrenceEndType === 'after'
    ? appointmentData.recurrenceEndAfter
    : Infinity;

  // Boucle principale de génération
  while (currentDate <= endDate && occurrenceCount < maxOccurrences) {
    if (appointmentData.recurrenceBase === 'daily') {
      // RÉCURRENCE JOURNALIÈRE
      appointments.push({
        ...appointmentData,
        date: new Date(currentDate),
        isRecurring: true,
        recurrenceSeriesId: recurrenceSeriesId
      });
      occurrenceCount++;

      // Avancer de N jours
      currentDate.setDate(currentDate.getDate() + appointmentData.recurrenceFrequency);

    } else if (appointmentData.recurrenceBase === 'weekly') {
      // RÉCURRENCE HEBDOMADAIRE (CORRIGÉE)
      const currentDay = currentDate.getDay();
      const isInSelectedDays = appointmentData.recurrenceDay.includes(currentDay);

      // Calculer les semaines écoulées depuis le début
      const millisecondsFromStart = currentDate - appointmentData.date;
      const weeksFromStart = Math.floor(millisecondsFromStart / (7 * 24 * 60 * 60 * 1000));

      // Vérifier si on est dans la bonne semaine de récurrence
      const isCorrectWeek = (weeksFromStart % appointmentData.recurrenceFrequency) === 0;

      if (isInSelectedDays && isCorrectWeek) {
        appointments.push({
          ...appointmentData,
          date: new Date(currentDate),
          isRecurring: true,
          recurrenceSeriesId: recurrenceSeriesId
        });
        occurrenceCount++;

        // Sortir si on a atteint le maximum
        if (occurrenceCount >= maxOccurrences) {
          break;
        }
      }

      // Avancer d'un jour
      currentDate.setDate(currentDate.getDate() + 1);

    } else if (appointmentData.recurrenceBase === 'monthly') {
      // RÉCURRENCE MENSUELLE
      if (shouldAddAppointment(currentDate, appointmentData)) {
        appointments.push({
          ...appointmentData,
          date: new Date(currentDate),
          isRecurring: true,
          recurrenceSeriesId: recurrenceSeriesId
        });
        occurrenceCount++;
      }

      // Passer au mois suivant
      currentDate = getNextRecurrenceDate(currentDate, appointmentData);
    }
  }

  return appointments;
};
